/**
 * In-memory response cache for the Overview page.
 *
 * Overview issues ~14 heavy requests per load. Without a cache, every navigation back
 * to the page re-runs all of them. This module holds the parsed responses for the
 * lifetime of the tab so a return visit only re-fetches the live endpoints.
 *
 * Deliberately dependency-free so that services (auth, header) can clear it without
 * creating an import cycle through the API layer.
 */

/** Safety net for long-lived sessions; the cache is normally dropped on logout. */
export const OVERVIEW_CACHE_TTL_MS = 60 * 60 * 1000;

/** Endpoints whose data must always come from the backend, never from cache. */
const LIVE_OVERVIEW_ENDPOINTS = new Set([
  'overview/hero',
  'overview/events',
  'overview/events_trend',
  'overview/traffic',
  'overview/traffic_trend',
]);

/**
 * Upper bound on retained responses. Each endpoint is keyed by resolution, so flipping
 * through every time preset would otherwise retain ~15 payloads per preset until the TTL
 * expired — and expired entries are only dropped when that exact key is read again.
 */
const MAX_OVERVIEW_CACHE_ENTRIES = 60;

const entries = new Map();

/** Drops expired entries, then the oldest ones if still over the cap. */
const evict = () => {
  const now = Date.now();

  entries.forEach((entry, key) => {
    if (now - entry.storedAt > OVERVIEW_CACHE_TTL_MS) {
      entries.delete(key);
    }
  });

  if (entries.size <= MAX_OVERVIEW_CACHE_ENTRIES) return;

  const oldestFirst = [...entries.entries()].sort(
    (a, b) => a[1].storedAt - b[1].storedAt
  );
  oldestFirst
    .slice(0, entries.size - MAX_OVERVIEW_CACHE_ENTRIES)
    .forEach(([key]) => entries.delete(key));
};

export const isLiveOverviewEndpoint = (endpoint) =>
  LIVE_OVERVIEW_ENDPOINTS.has(endpoint);

export const buildOverviewCacheKey = (endpoint, payload) =>
  `${endpoint}|${JSON.stringify(payload ?? {})}`;

/** Returns the cached value, or undefined when absent or expired. */
export const getOverviewCacheEntry = (key) => {
  const entry = entries.get(key);
  if (!entry) return undefined;

  if (Date.now() - entry.storedAt > OVERVIEW_CACHE_TTL_MS) {
    entries.delete(key);
    return undefined;
  }

  return entry.value;
};

export const hasOverviewCacheEntry = (key) => getOverviewCacheEntry(key) !== undefined;

export const setOverviewCacheEntry = (key, value) => {
  entries.set(key, { value, storedAt: Date.now() });
  evict();
};

export const clearOverviewCache = () => {
  entries.clear();
};
